import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Flame, ShoppingBag } from 'lucide-react';
import { BRAND } from '../data/mock';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import AccountMenu from './AccountMenu';

const links = [
  { to: '/', label: 'Home' },
  { to: '/menu', label: 'Menu' },
  { to: '/franchise', label: 'Franchise' },
  { to: '/global-expansion', label: 'Global' },
  { to: '/careers', label: 'Careers' },
  { to: '/about', label: 'About' }
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-orange-500/20' : 'bg-gradient-to-b from-black/80 to-transparent'}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="relative w-16 h-16 flex items-center justify-center transition group-hover:scale-105">
            <img src={BRAND.logo} alt="Zayka Sphere" className="w-full h-full object-contain drop-shadow-[0_4px_12px_rgba(249,115,22,0.45)]" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-xl tracking-wider text-white">ZAYKA <span className="text-orange-500">SPHERE</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400/80">Taste Ka Asli Global Station</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map(l => {
            const isActive = l.to === '/' ? location.pathname === '/' : location.pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} className={`relative text-sm font-semibold uppercase tracking-widest transition ${isActive ? 'text-orange-500' : 'text-white/85 hover:text-orange-400'}`}>
                {l.label}
                <span className={`absolute -bottom-1 left-0 h-[2px] bg-orange-500 transition-all ${isActive ? 'w-full' : 'w-0'}`}></span>
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => setCartOpen(true)} className="relative w-10 h-10 rounded-full bg-neutral-900 border border-white/10 hover:border-orange-500/60 flex items-center justify-center text-white/80 hover:text-orange-400 transition" aria-label="Open cart">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </button>
          <Link to="/menu">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider rounded-full px-6 h-10 btn-pulse">
              <Flame className="w-4 h-4 mr-2" /> Order Now
            </Button>
          </Link>
          <AccountMenu />
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button onClick={() => setCartOpen(true)} className="relative w-10 h-10 rounded-full bg-neutral-900 border border-white/10 flex items-center justify-center text-white" aria-label="Open cart">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </button>
          <button className="p-2 text-white" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-orange-500/20">
          <div className="px-5 py-6 flex flex-col gap-4">
            {links.map(l => (
              <Link key={l.to} to={l.to} className="text-base font-semibold uppercase tracking-widest text-white/90 hover:text-orange-500">
                {l.label}
              </Link>
            ))}
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-2 text-white/85 text-sm"><Phone className="w-4 h-4" /> {BRAND.phone}</a>
            <Link to="/menu" onClick={() => setOpen(false)}><Button className="w-full bg-orange-500 hover:bg-orange-600 rounded-full font-bold uppercase">Order Now</Button></Link>
            <AccountMenu mobile onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
    </header>
  );
}
